import { useMemo } from 'react';
import { CssBaseline, PaletteMode, Shadows } from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider, alpha } from '@mui/material/styles';
import { useAppTheme } from '@shared/theme/themeSettings/context/AppThemeContext';
import { observer } from 'mobx-react-lite';
import { getColor } from '@shared/theme/themeSettings/config';
import { shape } from './shape';
import { palette } from './palette';
import { typography } from './typography';
import { breakpoints } from './breakpoints';
import componentsOverride from './overrides';
import { shadows, customShadows } from './shadows';

interface Props {
  children?: React.ReactNode;
}

const lightPalette = {
  ...palette.light,
  mode: 'light' as PaletteMode
};
const darkPalette = {
  ...palette.dark,
  mode: 'dark' as PaletteMode
};

export const MuiThemeOverride: React.FC<Props> = observer(({ children }) => {
  const { themeMode, themeDirection, themeColor } = useAppTheme();
  const isLight = themeMode === 'light';

  const selectedColor = getColor(themeColor);

  const palette = isLight ? { ...lightPalette, primary: selectedColor } : { ...darkPalette, primary: selectedColor };

  const themeShadows: Shadows = isLight ? shadows.light : shadows.dark;

  const themeCustomShadows = isLight
    ? {
        ...customShadows.light,
        primary: `0 8px 16px 0 ${alpha(selectedColor.main, 0.24)}`
      }
    : { ...customShadows.dark, primary: `0 8px 16px 0 ${alpha(selectedColor.main, 0.24)}` };

  const themeOptions = useMemo(
    () => ({
      palette,
      shape,
      typography,
      breakpoints,
      direction: themeDirection,
      shadows: themeShadows,
      customShadows: themeCustomShadows
    }),
    [palette, themeDirection, themeShadows, themeCustomShadows]
  );

  const theme = createTheme(themeOptions);
  theme.components = componentsOverride(theme);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
});
